import background from "./assets/Sandwall.png";
import React, {useEffect, useState} from "react";
import {Alert, Button, Col, Form, Modal, Row, Spinner} from "react-bootstrap";

const apartmentNames = {
    sandwall: "Strandperle Sandwall",
    suederstrasse: "Süderstraße Gartenhaus",
};

async function apiRequest(path, options = {}) {
    const response = await fetch(path, {
        credentials: "same-origin",
        ...options,
        headers: {"Content-Type": "application/json", ...(options.headers || {})},
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || "Die Anfrage ist fehlgeschlagen.");
    return payload;
}

function Manage() {
    const [bookings, setBookings] = useState({sandwall: [], suederstrasse: []});
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [apartment, setApartment] = useState("sandwall");
    const [validated, setValidated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [jsonText, setJsonText] = useState("");
    const [showExport, setShowExport] = useState(false);

    const loadBookings = async () => {
        const data = await apiRequest("/api/bookings.php");
        setBookings(data);
    };

    useEffect(() => {
        const initialize = async () => {
            try {
                await loadBookings();
            } catch (requestError) {
                setError(requestError.message);
            } finally {
                setLoading(false);
            }
        };
        initialize();
    }, []);

    const addBooking = async (event) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();
        setValidated(true);
        if (!form.checkValidity()) return;
        if (endDate < startDate) {
            setError("Die Abreise darf nicht vor der Anreise liegen.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            await apiRequest("/api/bookings.php", {
                method: "POST",
                body: JSON.stringify({apartment, start: startDate, end: endDate}),
            });
            await loadBookings();
            setStartDate("");
            setEndDate("");
            setValidated(false);
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setSaving(false);
        }
    };

    const removeBooking = async (booking) => {
        if (!window.confirm(`Eintrag ${booking.start} – ${booking.ende} wirklich löschen?`)) return;
        setSaving(true);
        setError("");
        try {
            await apiRequest("/api/bookings.php", {
                method: "DELETE",
                body: JSON.stringify({id: booking.id}),
            });
            await loadBookings();
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setSaving(false);
        }
    };

    const exportBookings = () => {
        setJsonText(JSON.stringify(bookings, null, 2));
        setShowExport(true);
    };

    const header = (
        <div className="d-flex justify-content-center py-lg-5 backgroundHeader"
             style={{backgroundImage: `url(${background})`}}>
            <div className="mx-auto"><h1 className="navHeadline">Verwalten</h1></div>
        </div>
    );

    if (loading) {
        return <div>{header}<div className="container text-center"><Spinner animation="border"/></div></div>;
    }

    return (
        <div>
            {header}
            <div className="container pb-5">
                {error && <Alert variant="danger">{error}</Alert>}
                <Form noValidate validated={validated} onSubmit={addBooking}>
                    <Row className="mb-4 align-items-end">
                        <Form.Group as={Col} md="3">
                            <Form.Label>Anreise</Form.Label>
                            <Form.Control required type="date" value={startDate}
                                          onChange={event => setStartDate(event.target.value)}/>
                        </Form.Group>
                        <Form.Group as={Col} md="3">
                            <Form.Label>Abreise</Form.Label>
                            <Form.Control required type="date" min={startDate} value={endDate}
                                          onChange={event => setEndDate(event.target.value)}/>
                        </Form.Group>
                        <Form.Group as={Col} md="3">
                            <Form.Label>Wohnung</Form.Label>
                            <Form.Select required value={apartment}
                                         onChange={event => setApartment(event.target.value)}>
                                {Object.keys(apartmentNames).map(key => (
                                    <option key={key} value={key}>{apartmentNames[key]}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group as={Col} md="2">
                            <Button type="submit" className="w-100" disabled={saving}>Hinzufügen</Button>
                        </Form.Group>
                    </Row>
                </Form>
                <Row>
                    {Object.keys(apartmentNames).map(key => (
                        <Col md="6" key={key} className="mb-4 text-center">
                            <h2 className="mb-3">{apartmentNames[key]}</h2>
                            {bookings[key].length === 0 && <p>Noch keine Einträge.</p>}
                            {bookings[key].map(booking => (
                                <div key={booking.id} className="mb-1">
                                    {booking.start} – {booking.ende}{" "}
                                    <Button variant="link" className="p-0 text-decoration-none"
                                            aria-label="Eintrag löschen" disabled={saving}
                                            onClick={() => removeBooking(booking)}>🗑</Button>
                                </div>
                            ))}
                        </Col>
                    ))}
                </Row>
                <div className="text-center">
                    <Button variant="outline-secondary" style={{minWidth: "12rem"}} onClick={exportBookings}>
                        Exportieren
                    </Button>
                </div>
            </div>
            <Modal show={showExport} onHide={() => setShowExport(false)} size="lg">
                <Modal.Header closeButton><Modal.Title>JSON-Sicherung</Modal.Title></Modal.Header>
                <Modal.Body><Form.Control as="textarea" rows={14} readOnly value={jsonText}/></Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => navigator.clipboard.writeText(jsonText)}>Kopieren</Button>
                    <Button variant="secondary" onClick={() => setShowExport(false)}>Schließen</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default Manage;
